import { DriverDto, ProductLine, RegionDto, TruckDetailsDto } from '@trucktrax/trucktrax-ts-common';
import { Dispatch } from 'redux';
import { overrideMapFilter } from './mapFilterActions';
import {
  CLEAR_SELECTED_MARKER,
  CLOSE_ALL_POPUPS,
  CLOSE_POPUP,
  OPEN_POPUP,
  REFRESH_POSITIONS,
  SELECT_MARKER,
  SET_IS_PANNED,
  SET_POSITION_LIST,
  SET_INCOMING_POSITION_LIST,
  UPDATE_POSITION,
  UPDATE_INCOMING_POSITION,
  ACTIVE_PIN_MAP,
  SET_PIN_RADIUS,
  SET_ACTIVE_MARKERS,
  REMOVE_POSITION,
  REMOVE_INCOMING_POSITION,
} from '../../constants/actionConstants';
import { TRUCKTRAX_GEO_FLAG } from '../../constants/appConstants';
import { MarkerType, MARKER_TYPE_ORDER, MARKER_TYPE_TRUCK } from '../../constants/mapConstants';
import { MapMarker, PositionDetailsDto } from '../../types';
import { isFeatureFlagEnabled } from '../../util/featureFlagUtil';

export const setPositionList = (positionList: PositionDetailsDto[]) => ({
  type: SET_POSITION_LIST,
  payload: positionList,
});

export const setIncomingPositionList = (positionList: PositionDetailsDto[]) => ({
  type: SET_INCOMING_POSITION_LIST,
  payload: positionList,
});

export const updatePosition = (position: PositionDetailsDto) => ({
  type: UPDATE_POSITION,
  payload: position,
});

export const updateIncomingPosition = (position: PositionDetailsDto) => ({
  type: UPDATE_INCOMING_POSITION,
  payload: position,
});

export const removePosition = (positionId: number) => ({
  type: REMOVE_POSITION,
  payload: positionId,
});

export const removeIncomingPosition = (positionId: number) => ({
  type: REMOVE_INCOMING_POSITION,
  payload: positionId,
});

interface TruckDetailsDtoNew extends TruckDetailsDto {
  trackedObject?: {
    isExternal: boolean;
    truckAlias?: string;
  };
  location?: { latitude: number; longitude: number };
}

export const MarkerSelectedFrom = {
  VehicleAndOrderSearch: 'vehicle-and-order-search',
  TruckFolderList: 'truck-folder-list',
  MapMarker: 'map-marker',
} as const;

export type MarkerSelectedFromSource = (typeof MarkerSelectedFrom)[keyof typeof MarkerSelectedFrom];

export interface MarkerInfo {
  id: string;
  markerId: string;
  type: MarkerType;
  isPannedTo?: boolean;
  lat?: number;
  lng?: number;
  isExternal?: boolean;
  markerData: {
    markerId: string;
    type: MarkerType;
    driverDetails: DriverDto;
    truckDetails: TruckDetailsDtoNew;
    positionDetails: PositionDetailsDto;
  };
  routeHistoryMarker?: boolean;
  selectedFrom?: MarkerSelectedFromSource;
}

type GetState = () => {
  currentRegion: RegionDto;
  selectedProductLine: ProductLine;
  userPermission?: any;
};

export interface PinLocationInfo {
  pinMode?: boolean;
  pinDropped?: boolean;
  removePin?: boolean;
  location?: string;
  lat?: number;
  lng?: number;
}

export const selectMarker =
  (marker: MarkerInfo, fromTickets = false) =>
  (dispatch: Dispatch, getState: GetState) => {
    const { currentRegion, selectedProductLine, userPermission } = getState();
    dispatch({
      type: SELECT_MARKER,
      payload: marker,
    });
    dispatch({
      type: OPEN_POPUP,
      payload: {
        markerId: marker.markerId,
        regionId: currentRegion?.id,
        productLine: selectedProductLine,
      },
    });
    dispatch({
      type: SET_IS_PANNED,
    });

    let truckIsExternal;
    if (marker.type === MARKER_TYPE_TRUCK) {
      truckIsExternal =
        marker.markerData?.truckDetails?.trackedObject?.isExternal ??
        (marker as unknown as MapMarker)?.data?.truckDetails?.isExternal ??
        marker.isExternal;
    }

    // Check if user has the TruckTrax Geo feature flag before allowing external trucks to be shown
    const isTruckTraxGeoEnabled = isFeatureFlagEnabled(
      userPermission || {},
      currentRegion?.url || '',
      TRUCKTRAX_GEO_FLAG,
    );

    if (!fromTickets) {
      if (marker.type === MARKER_TYPE_ORDER) {
        dispatch(overrideMapFilter({ showJobsites: true }));
      } else if (marker.type === MARKER_TYPE_TRUCK && truckIsExternal && isTruckTraxGeoEnabled) {
        dispatch(overrideMapFilter({ showExtTrucks: true }));
      } else if (marker.type === MARKER_TYPE_TRUCK && !truckIsExternal) {
        dispatch(overrideMapFilter({ showTrucks: true }));
      }
    } else if (marker?.isExternal && isTruckTraxGeoEnabled) {
      dispatch(overrideMapFilter({ showExtTrucks: true }));
    } else if (!marker?.isExternal) {
      dispatch(overrideMapFilter({ showTrucks: true }));
    }
  };

export const clearSelectedMarker = () => ({
  type: CLEAR_SELECTED_MARKER,
});

export const setActiveMarkers = (markers: MapMarker[]) => (dispatch: Dispatch) => {
  dispatch({
    type: SET_ACTIVE_MARKERS,
    payload: markers,
  });
};

export const setIsPanned = () => ({
  type: SET_IS_PANNED,
});

export const openPopup = (markerId: string, regionId: string, productLine: string) => ({
  type: OPEN_POPUP,
  payload: {
    markerId,
    regionId,
    productLine,
  },
});

export const closePopup = (markerId: string, regionId: string, productLine: string) => ({
  type: CLOSE_POPUP,
  payload: {
    markerId,
    regionId,
    productLine,
  },
});

export const closeAllPopups = () => ({
  type: CLOSE_ALL_POPUPS,
});

export const refreshPositions = () => ({
  type: REFRESH_POSITIONS,
});

/**
 * Sets the pin map information
 * @param {PinLocationInfo} pinInfo pin Info map
 */
export const setPinMap = (pinInfo: PinLocationInfo) => ({
  type: ACTIVE_PIN_MAP,
  payload: pinInfo,
});

export const setPinRadius = (radius: string) => ({
  type: SET_PIN_RADIUS,
  payload: radius,
});
